/**
 * store/auth.ts
 * Zustand auth store with users management, roles, and per-store access control.
 * - Default admin user on first run: ADMIN / ADMIN (can be changed later).
 * - Roles:
 *   - 'admin': full control
 *   - 'entry': limited: can add income/expense only for current month, no totals visibility, no edit/delete for other periods.
 *   - 'viewer': read-only view; cannot add/edit/delete or manage gateways.
 * - Per-user store access: entry/viewer users must have assigned storeIds; admin implicitly has access to all.
 * Persistence: localStorage.
 */

import { create } from 'zustand'

/** Role for a user */
export type UserRole = 'admin' | 'entry' | 'viewer'

/** User entity with optional per-store access list */
export interface User {
  id: string
  username: string
  password: string
  role: UserRole
  createdAt: string
  /** List of store IDs this user can access (optional for admin). */
  storeIds?: string[]
}

/** Auth state with users and current session */
interface AuthState {
  users: User[]
  currentUserId: string | null

  // Session
  login: (username: string, password: string) => { ok: boolean; reason?: string }
  logout: () => void

  // Users management (admin only in UI)
  addUser: (input: {
    username: string
    password: string
    role: UserRole
    storeIds?: string[]
  }) => { ok: boolean; reason?: string }
  updateUser: (
    id: string,
    patch: Partial<{ username: string; role: UserRole; storeIds: string[] }>
  ) => void
  changePassword: (id: string, newPassword: string) => void
  removeUser: (id: string) => void

  // Helpers
  getCurrentUser: () => User | null
  hasAccessToStore: (storeId: string) => boolean

  // Emergency reset
  /**
   * Resets the auth store to a single recovery admin user:
   * username: '0500005377'
   * password: '0505197451'
   * Clears current session.
   */
  resetToDefaultAdmin: () => void
}

const STORAGE_KEY = 'auth-store-v1'

function load(): { users: User[]; currentUserId: string | null } {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) {
      const parsed = JSON.parse(raw)
      if (Array.isArray(parsed.users)) {
        return { users: parsed.users, currentUserId: parsed.currentUserId ?? null }
      }
    }
  } catch {
    // ignore
  }
  // First run: create default ADMIN/ADMIN
  const defaultUser: User = {
    id: crypto.randomUUID(),
    username: 'ADMIN',
    password: 'ADMIN',
    role: 'admin',
    createdAt: new Date().toISOString(),
    // Admin: storeIds not required (full access)
  }
  return { users: [defaultUser], currentUserId: null }
}

function save(state: { users: User[]; currentUserId: string | null }) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  } catch {
    // ignore
  }
}

/** useAuthStore
 * Manages users and current session.
 */
export const useAuthStore = create<AuthState>((set, get) => {
  const persisted = load()

  return {
    users: persisted.users,
    currentUserId: persisted.currentUserId,

    login: (username, password) => {
      const state = get()
      const user = state.users.find(
        (u) => u.username.toLowerCase() === username.trim().toLowerCase() && u.password === password
      )
      if (!user) return { ok: false, reason: 'بيانات الدخول غير صحيحة' }
      set({ currentUserId: user.id })
      save({ users: state.users, currentUserId: user.id })
      return { ok: true }
    },

    logout: () => {
      const state = get()
      set({ currentUserId: null })
      save({ users: state.users, currentUserId: null })
    },

    addUser: (input) => {
      const state = get()
      const cleanName = input.username.trim()
      if (!cleanName || !input.password.trim()) {
        return { ok: false, reason: 'اسم المستخدم وكلمة المرور مطلوبة' }
      }
      if (state.users.some((u) => u.username.toLowerCase() === cleanName.toLowerCase())) {
        return { ok: false, reason: 'اسم المستخدم مستخدم مسبقاً' }
      }
      const user: User = {
        id: crypto.randomUUID(),
        username: cleanName,
        password: input.password,
        role: input.role,
        createdAt: new Date().toISOString(),
        // entry/viewer must have storeIds; admin ignores storeIds
        storeIds: input.role === 'admin' ? undefined : Array.from(new Set(input.storeIds || [])),
      }
      const users = [...state.users, user]
      set({ users })
      save({ users, currentUserId: state.currentUserId })
      return { ok: true }
    },

    updateUser: (id, patch) => {
      const state = get()
      const idx = state.users.findIndex((u) => u.id === id)
      if (idx === -1) return
      const cur = state.users[idx]
      const nextRole = patch.role !== undefined ? patch.role : cur.role
      const next: User = {
        ...cur,
        username: patch.username !== undefined ? (patch.username?.trim() || cur.username) : cur.username,
        role: nextRole,
        // If role changed to admin, clear storeIds; else set from patch or keep existing (ensure unique)
        storeIds:
          nextRole === 'admin'
            ? undefined
            : patch.storeIds
            ? Array.from(new Set(patch.storeIds))
            : cur.storeIds || [],
      }
      const users = [...state.users]
      users[idx] = next
      set({ users })
      save({ users, currentUserId: state.currentUserId })
    },

    changePassword: (id, newPassword) => {
      const state = get()
      const idx = state.users.findIndex((u) => u.id === id)
      if (idx === -1) return
      const cur = state.users[idx]
      const next: User = { ...cur, password: newPassword }
      const users = [...state.users]
      users[idx] = next
      set({ users })
      save({ users, currentUserId: state.currentUserId })
    },

    removeUser: (id) => {
      const state = get()
      // Prevent removing last admin
      const target = state.users.find((u) => u.id === id)
      if (!target) return
      if (target.role === 'admin') {
        const admins = state.users.filter((u) => u.role === 'admin')
        if (admins.length <= 1) return
      }
      const users = state.users.filter((u) => u.id !== id)
      let currentUserId = state.currentUserId
      if (currentUserId === id) currentUserId = null
      set({ users, currentUserId })
      save({ users, currentUserId })
    },

    getCurrentUser: () => {
      const state = get()
      return state.users.find((u) => u.id === state.currentUserId) || null
    },

    /**
     * hasAccessToStore
     * Returns true if the current user can access the given store.
     * - Admin: true for all.
     * - Entry/Viewer: storeId must be included in storeIds.
     */
    hasAccessToStore: (storeId) => {
      const user = get().getCurrentUser()
      if (!user) return false
      if (user.role === 'admin') return true
      return Boolean(user.storeIds?.includes(storeId))
    },

    /**
     * resetToDefaultAdmin
     * Emergency reset: replaces all users with a single recovery admin user and clears the session.
     * New credentials:
     * - username: '0500005377'
     * - password: '0505197451'
     */
    resetToDefaultAdmin: () => {
      const defaultUser: User = {
        id: crypto.randomUUID(),
        username: '0500005377',
        password: '0505197451',
        role: 'admin',
        createdAt: new Date().toISOString(),
      }
      set({ users: [defaultUser], currentUserId: null })
      save({ users: [defaultUser], currentUserId: null })
    },
  }
})